import React from 'react'
// libs
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { change, formValueSelector } from 'redux-form'
// components
import { ReduxSwitch } from 'components/FormElements'
// constants
import { Forms } from 'constants/index'
// types
import { IField, IRiskTemplate } from 'types/graphql'

const Requirements = styled.span`
  font-size: 12px;
  color: #757575;
`

interface Props {
  field: IField
  parentId: string
  isRelated: boolean
  riskTemplate: IRiskTemplate
}

const selector = formValueSelector(Forms.RISK_VIEW_FORM)

const FormField: React.FC<Props> = ({ field, parentId, isRelated, riskTemplate }) => {
  const dispatch = useDispatch()

  const [disabled, setDisabled] = React.useState(false)

  const fieldLink = riskTemplate.fieldLinks.find(
    ({ field: { id }, parentFieldId }) => id === field.id && parentFieldId === field.parentFieldId
  )

  const fields = useSelector((state) => selector(state, 'fields') || [])
  const isActive = useSelector((state) => selector(state, `fields.${fieldLink.id}.active`))

  const fieldGroups = riskTemplate.fieldGroups
    .filter(
      ({ fieldId, groupedByEntityId, parentFieldId }) =>
        fieldId === field.id && groupedByEntityId !== riskTemplate.id && parentFieldId === field.parentFieldId
    )
    .map(({ groupedByEntityId }) => groupedByEntityId)

  const groupedBy = riskTemplate.fieldLinks.filter(
    ({ field: { entityId }, parentFieldId, primary }) =>
      primary &&
      parentFieldId === field.parentFieldId &&
      (fieldLink.primary ? fieldGroups.some((id) => id === entityId) : entityId === field.entityId)
  )

  const groupedByString = groupedBy.map(({ fieldLabel }) => fieldLabel).join(' or ')

  const onChange = (_e: React.ChangeEvent, value: boolean) => {
    dispatch(
      change(Forms.RISK_VIEW_FORM, `fields.${fieldLink.id}`, {
        active: value,
        fieldId: field.id,
        fieldLinkId: fieldLink.id,
        parentFieldId: parentId,
        ...(isRelated && { riskTemplateId: riskTemplate.id })
      })
    )
  }

  React.useEffect(() => {
    if (disabled && groupedBy.some(({ id }) => fields[id]?.active)) {
      setDisabled(false)
    }

    if (!disabled && groupedBy.length && groupedBy.every(({ id }) => !fields[id]?.active)) {
      setDisabled(true)

      if (isActive) {
        dispatch(change(Forms.RISK_VIEW_FORM, `fields.${fieldLink.id}.active`, false))
      }
    }
  }, [fields])

  return (
    <>
      <ReduxSwitch
        label={field.label}
        disabled={disabled}
        preventInitialChange
        listenOnChange={onChange}
        justifyContent='space-between'
        name={`fields.${fieldLink.id}.active`}
      />
      {groupedByString && <Requirements>{`Requires ${groupedByString}`}</Requirements>}
    </>
  )
}

export default FormField
